import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, Sparkles, Package, Trophy, Heart, Zap } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RobotRecommendationWidget from "@/components/RobotRecommendationWidget";
import RobotPageNav from "@/components/RobotPageNav";
import swiftbotImg from "@/assets/robots/swiftbot-hygiene.jpeg";

const SwiftBot = () => {
  const specifications = [
    { icon: <Shield className="w-5 h-5" />, label: "Hygiene", value: "Medical-Grade" },
    { icon: <Package className="w-5 h-5" />, label: "Configuration", value: "Flexible Modules" },
    { icon: <Sparkles className="w-5 h-5" />, label: "Sterilization", value: "UV-C Ready" },
    { icon: <Trophy className="w-5 h-5" />, label: "Award", value: "iF Design 2023" }
  ];

  const keyFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Hygienic Delivery Module",
      description: "Medical-grade enclosed compartments ensure sterile transport of medications, lab samples, and medical supplies with contamination prevention."
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Flexible Configuration",
      description: "Multiple attachment modes accommodate diverse medical needs from medication delivery to equipment transport."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "UV Sterilization Ready",
      description: "Optional UV-C disinfection capability for high-hygiene environments ensures maximum safety and cleanliness."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Sealed Compartments",
      description: "Prevent contamination during transport with secure, sealed compartments meeting medical facility standards."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Hospital-Grade Materials",
      description: "Easy-to-clean surfaces meet stringent medical standards for infection control and patient safety."
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: "iF Design Award 2023",
      description: "Internationally recognized design excellence combining functionality with aesthetic appeal for healthcare environments."
    }
  ];

  const applications = [
    {
      title: "Hospitals & Medical Centers",
      description: "Medication delivery, lab sample transport, and medical supply distribution with hygienic sealed compartments."
    },
    {
      title: "Pharmacies",
      description: "Prescription delivery and inventory transport maintaining sterile conditions throughout the facility."
    },
    {
      title: "Long-Term Care Facilities",
      description: "Medication rounds, supply delivery, and meal transport for assisted living and nursing homes."
    },
    {
      title: "Laboratories",
      description: "Sterile sample transport between departments with contamination prevention and secure handling."
    },
    {
      title: "Clean Rooms",
      description: "Material transport in controlled environments requiring strict contamination control protocols."
    },
    {
      title: "Research Facilities",
      description: "Equipment and sample delivery in research environments with specialized hygiene requirements."
    }
  ];

  return (
    <>
      <SEO
        title="SwiftBot - Hygienic Delivery Robot for Medical Industry | Neil AI"
        description="iF Design Award 2023 winner. Hygienic delivery robot with medical-grade compartments, UV sterilization, and flexible configuration for healthcare facilities."
        keywords="SwiftBot, medical robot, hygienic delivery, hospital robot, UV sterilization, healthcare automation"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <RobotRecommendationWidget />
        
        <main className="container mx-auto px-4 pt-12 pb-8 md:pt-16 md:pb-12">
          <RobotPageNav />

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
            <div className="space-y-6">
              <div>
                <Badge className="mb-4">Hygienic Delivery for Medical Industry</Badge>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">SwiftBot</h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Award-winning hygienic delivery robot with medical-grade compartments and UV sterilization for healthcare facilities. iF Design Award 2023 winner.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <span className="text-xs font-semibold">{spec.label}</span>
                      </div>
                      <p className="text-xs md:text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button asChild size="lg" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">Book a Demo</a>
                </Button>
                <Button asChild variant="outline" size="lg" className="flex-1">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg" className="flex-1">
                  <a href="tel:+13369624539">Call Us: (336) 962-4539</a>
                </Button>
              </div>

              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold mb-1 text-sm md:text-base">ROI Impact</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Reduce medical transport labor by 55% while improving safety and hygiene compliance. ROI typically within 12-16 months.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="relative">
              <div className="aspect-square md:aspect-auto md:h-full rounded-lg overflow-hidden bg-white/5 flex items-center justify-center p-4 md:p-8">
                <img src={swiftbotImg} alt="SwiftBot - Hygienic Delivery Robot" className="w-full h-full object-cover" />
              </div>
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">iF DESIGN AWARD 2023</Badge>
            </div>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Key Features</CardTitle>
                  <CardDescription>Medical-grade hygiene and safety</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {keyFeatures.map((feature, idx) => (
                      <div key={idx} className="flex gap-3 md:gap-4">
                        <div className="text-primary flex-shrink-0">{feature.icon}</div>
                        <div>
                          <h4 className="font-bold mb-1 md:mb-2 text-sm md:text-base">{feature.title}</h4>
                          <p className="text-xs md:text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specs" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader><CardTitle className="font-orbitron">Technical Specifications</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    {[
                      ["Hygiene Module", "Medical-Grade Enclosed Compartments"],
                      ["Sterilization", "UV-C Ready (Optional)"],
                      ["Configuration", "Flexible Multi-Attachment"],
                      ["Materials", "Hospital-Grade Easy-Clean"],
                      ["Award", "iF Design Award 2023 Winner"],
                      ["Safety", "Sealed Contamination Prevention"]
                    ].map(([label, value], idx) => (
                      <div key={idx} className={`grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 ${idx < 5 ? 'pb-3 md:pb-4 border-b border-[hsl(var(--line))]' : ''}`}>
                        <span className="font-semibold text-sm md:text-base">{label}</span>
                        <span className="text-sm md:text-base">{value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Ideal Applications</CardTitle>
                  <CardDescription>Perfect for healthcare and medical environments</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="p-3 md:p-4 rounded-lg border border-[hsl(var(--line))] hover:border-primary/50 transition-colors">
                        <h4 className="font-bold mb-2 text-sm md:text-base">{app.title}</h4>
                        <p className="text-xs md:text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <Card className="glass-effect border-primary/30 bg-primary/5 mt-8 md:mt-12">
            <CardContent className="p-6 md:p-8 text-center">
              <h2 className="font-orbitron font-bold text-xl md:text-2xl mb-4">Ready to Enhance Medical Delivery Safety?</h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Transform healthcare operations with SwiftBot's award-winning hygienic delivery technology.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">Schedule a Demo</a>
                </Button>
                <Button asChild variant="secondary" size="lg">
                  <Link to="/robot-recommender">Find Your Perfect Robot</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default SwiftBot;
